import React from 'react';
import { humanizeKey } from '../../utils/humanizeKey';
import { hasMultipleOptions, getComponentOptions, getOptionTotal } from '../Quotes/optionUtils';
import { 
  Palmtree,
  Waves,
  Sun,
  Umbrella,
  Sparkles,
  Wine,
  Dumbbell,
  Users,
  Calendar,
  Star,
  MapPin,
  CheckCircle2,
  Shell,
  Plane,
  Hotel,
  Ship,
  Utensils,
  Mail,
  Phone
} from 'lucide-react';
import { TEMPLATE_PALETTES } from './templatePalettes';

const PALETTE = TEMPLATE_PALETTES.tropical_escape;

const TropicalEscapeTemplate = ({ quote, onPayment, isAgentPreview = false }) => {
  if (!quote) {
    return null;
  }

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Parse date in local timezone to avoid UTC offset issues
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get client name
  const getClientName = () => {
    if (quote.client?.firstName && quote.client?.lastName) {
      return `${quote.client.firstName} ${quote.client.lastName}`;
    }
    return 'Valued Guest';
  };

  // Extract resort details from hotel components
  const getResortDetails = () => {
    const hotelComponent = quote.components?.find(c => 
      c.type?.toLowerCase().includes('hotel') || 
      c.type?.toLowerCase().includes('resort') ||
      c.title?.toLowerCase().includes('resort') ||
      c.title?.toLowerCase().includes('hotel')
    );
    
    if (hotelComponent?.data) {
      return {
        resortName: hotelComponent.data.property_name || hotelComponent.data.resort_name || hotelComponent.title,
        roomCategory: hotelComponent.data.room_type || hotelComponent.data.room_category || null,
        view: hotelComponent.data.view || null,
        amenities: hotelComponent.data.amenities || null,
        checkIn: hotelComponent.data.check_in_date || null,
        checkOut: hotelComponent.data.check_out_date || null,
        location: hotelComponent.data.location || hotelComponent.data.destination || null,
        images: hotelComponent.data.images || hotelComponent.data.photos || null
      };
    }
    return {};
  };

  const resortDetails = getResortDetails();

  return (
    <div className="min-h-screen" style={{ ...PALETTE, backgroundColor: 'var(--background)' }}>
      {/* Tropical Header */}
      <div className="relative bg-[var(--primary)] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10"><Palmtree className="w-32 h-32 text-[var(--background)]" /></div>
          <div className="absolute bottom-10 right-10"><Sun className="w-40 h-40 text-[var(--background)]" /></div>
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Waves className="w-8 h-8 text-[var(--background)] animate-pulse" />
            <span className="text-[var(--background)] text-sm tracking-widest uppercase">Paradise Awaits</span>
            <Waves className="w-8 h-8 text-[var(--background)] animate-pulse" />
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-[var(--background)] mb-4 drop-shadow-lg">
            {quote.quoteTitle || 'Your Tropical Escape'}
          </h1>
          
          <p className="text-[var(--background)]/90 text-xl font-light">
            Curated for {getClientName()}
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-16">
        
        {/* Resort Showcase - Hero Section */}
        {resortDetails.resortName && (
          <div className="mb-16 bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Resort Header */}
            <div className="bg-[var(--muted)] p-10 text-center border-b-4 border-[var(--primary)]">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Star className="w-8 h-8 text-[var(--muted-foreground)]" />
                <Star className="w-8 h-8 text-[var(--muted-foreground)]" />
                <Star className="w-8 h-8 text-[var(--muted-foreground)]" />
              </div>
              <h2 className="text-4xl font-bold text-[var(--foreground)] mb-2">
                {resortDetails.resortName}
              </h2>
              {resortDetails.location && (
                <div className="flex items-center justify-center gap-2 text-[var(--muted-foreground)]">
                  <MapPin className="w-5 h-5" />
                  <span className="text-lg">{resortDetails.location}</span>
                </div>
              )}
            </div>

            {/* Resort Details Grid */}
            <div className="p-10">
              <div className="grid md:grid-cols-2 gap-8 mb-10">
                {/* Room Category */}
                {resortDetails.roomCategory && (
                  <div className="bg-[var(--muted)] rounded-xl p-8 border-2 border-[var(--border)]">
                    <div className="flex items-center gap-3 mb-4">
                      <Umbrella className="w-8 h-8 text-[var(--muted-foreground)]" />
                      <h3 className="text-2xl font-bold text-[var(--foreground)]">Your Accommodation</h3>
                    </div>
                    <p className="text-3xl font-semibold text-[var(--foreground)]">{resortDetails.roomCategory}</p>
                    {resortDetails.view && (
                      <p className="text-[var(--muted-foreground)] mt-2 text-lg">{resortDetails.view}</p>
                    )}
                  </div>
                )}

                {/* Stay Duration */}
                {resortDetails.checkIn && resortDetails.checkOut && (
                  <div className="bg-[var(--muted)] rounded-xl p-8 border-2 border-[var(--border)]">
                    <div className="flex items-center gap-3 mb-4">
                      <Calendar className="w-8 h-8 text-[var(--muted-foreground)]" />
                      <h3 className="text-2xl font-bold text-[var(--foreground)]">Your Stay</h3>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <span className="text-[var(--muted-foreground)] text-sm uppercase tracking-wide">Check-In</span>
                        <p className="text-xl font-semibold text-[var(--foreground)]">{formatDate(resortDetails.checkIn)}</p>
                      </div>
                      <div>
                        <span className="text-[var(--muted-foreground)] text-sm uppercase tracking-wide">Check-Out</span>
                        <p className="text-xl font-semibold text-[var(--foreground)]">{formatDate(resortDetails.checkOut)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Amenities Highlights */}
              <div className="bg-[var(--muted)] rounded-xl p-8 border-2 border-[var(--border)]">
                <div className="flex items-center gap-3 mb-6">
                  <Sparkles className="w-8 h-8 text-[var(--muted-foreground)]" />
                  <h3 className="text-2xl font-bold text-[var(--foreground)]">Resort Amenities</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-md">
                      <Waves className="w-8 h-8 text-[var(--muted-foreground)]" />
                    </div>
                    <p className="text-[var(--foreground)] font-semibold text-sm sm:text-base">Beach Access</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-md">
                      <Wine className="w-8 h-8 text-[var(--muted-foreground)]" />
                    </div>
                    <p className="text-[var(--foreground)] font-semibold text-sm sm:text-base">Spa Services</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-md">
                      <Dumbbell className="w-8 h-8 text-[var(--muted-foreground)]" />
                    </div>
                    <p className="text-[var(--foreground)] font-semibold text-sm sm:text-base">Fitness Center</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-md">
                      <Sun className="w-8 h-8 text-[var(--muted-foreground)]" />
                    </div>
                    <p className="text-[var(--foreground)] font-semibold text-sm sm:text-base">Pool & Lounge</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Additional Experiences */}
        {quote.components && quote.components.length > 0 && (
          <div className="mb-16">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-[var(--foreground)] mb-2">Included Experiences</h2>
              <p className="text-[var(--muted-foreground)]">Everything you need for the perfect getaway</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {quote.components.map((component, index) => (
                <div
                  key={component.id || index}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow border-2 border-[var(--border)]"
                >
                  <div className="bg-[var(--primary)] p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-bold text-[var(--background)]">
                        {component.title || `Experience ${index + 1}`}
                      </h3>
                      <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-white text-xs font-semibold uppercase">
                        {component.type || 'Included'}
                      </span>
                    </div>
                  </div>

                  <div className="p-6">
                    {component.data && Object.keys(component.data).length > 0 && (
                      <div className="space-y-3 mb-4">
                        {Object.entries(component.data).map(([key, value]) => {
                          if (!value || key === 'isOption' || key === 'optionDescription' || key === 'images' || key === 'photos') return null;
                          
                          // Check if this is a price field
                          const isPriceField = key.toLowerCase().includes('price') || 
                                               key.toLowerCase().includes('cost') || 
                                               key.toLowerCase().includes('fee') ||
                                               key.toLowerCase().includes('total') ||
                                               key.toLowerCase().includes('deposit');
                          const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);
                          
                          let displayValue = String(value);
                          if (isPriceField && isNumeric) {
                            displayValue = formatCurrency(parseFloat(value));
                          }
                          
                          return (
                            <div key={key} className="flex justify-between items-center py-2 border-b border-[var(--border)]">
                              <span className="text-[var(--muted-foreground)] text-sm uppercase tracking-wide">
                                {humanizeKey(key)}
                              </span>
                              <span className="text-[var(--foreground)] font-semibold">
                                {displayValue}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className="pt-4 border-t-2 border-[var(--border)] flex justify-between items-center">
                      <span className="text-[var(--muted-foreground)] font-semibold">Investment</span>
                      <span className="text-2xl font-bold text-[var(--foreground)]">{formatCurrency(component.price)}</span>
                    </div>

                    {/* Component Photos */}
                    {component.photos && component.photos.length > 0 && (
                      <div className="mt-6">
                        <h4 className="text-sm font-bold text-[var(--foreground)] mb-3 uppercase tracking-wide flex items-center gap-2">
                          <span>📸</span> Photos
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {component.photos.map((photo, photoIndex) => (
                            <div 
                              key={photoIndex} 
                              className="relative group cursor-pointer rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow border-2 border-[var(--border)] bg-white"
                              onClick={() => window.open(photo.url, '_blank')}
                            >
                              <img 
                                src={photo.url} 
                                alt={photo.name || 'Component photo'} 
                                className="w-full h-auto object-contain"
                                style={{ maxHeight: '300px' }}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-[var(--primary)]/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                                <span className="text-[var(--background)] text-sm font-semibold">{photo.name || 'View Photo'}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Component Documents */}
                    {component.documents && component.documents.length > 0 && (
                      <div className="mt-6">
                        <h4 className="text-sm font-bold text-[var(--foreground)] mb-3 uppercase tracking-wide flex items-center gap-2">
                          <span>📋</span> Documents
                        </h4>
                        <div className="space-y-2">
                          {component.documents.map((doc, docIndex) => (
                            <a 
                              key={docIndex}
                              href={doc.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 bg-[var(--muted)] hover:brightness-95 px-4 py-2 rounded-xl transition-colors border border-[var(--border)]"
                            >
                              <span className="text-lg">📄</span>
                              <span className="text-[var(--foreground)] text-sm font-semibold">{doc.name}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {hasMultipleOptions(component) && (
                      <div className="mt-6" style={{ paddingLeft: '1rem', paddingRight: '1rem', paddingBottom: '1rem' }}>
                        <h4 className="text-sm font-bold mb-3 uppercase tracking-wide" style={{ color: 'var(--foreground)' }}>Choose Your Option</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {getComponentOptions(component).map((option, optIndex) => (
                            <div key={option.id || optIndex} className="rounded-xl overflow-hidden" style={{ border: `2px solid ${optIndex === 0 ? 'var(--accent)' : 'var(--border)'}` }}>
                              {option.photoUrl && (
                                <img src={option.photoUrl} alt={option.label || 'Option'} className="w-full h-28 object-cover" />
                              )}
                              <div className="p-4">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <span className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>{option.label || `Option ${optIndex + 1}`}</span>
                                  {option.badge && (
                                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: 'var(--muted)', color: 'var(--accent)' }}>{option.badge}</span>
                                  )}
                                </div>
                                {option.description && (
                                  <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>{option.description}</p>
                                )}
                                <p className="text-lg font-bold" style={{ color: 'var(--accent)' }}>{formatCurrency(getOptionTotal(option))}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Special Notes */}
        {quote.notes && (
          <div className="mb-16 bg-[var(--muted)] rounded-xl border-2 border-[var(--border)] p-8">
            <div className="flex items-center gap-3 mb-4">
              <Palmtree className="w-6 h-6 text-[var(--muted-foreground)]" />
              <h3 className="text-2xl font-bold text-[var(--foreground)]">Important Information</h3>
            </div>
            <p className="text-[var(--foreground)] leading-relaxed whitespace-pre-wrap">
              {quote.notes}
            </p>
          </div>
        )}

        {/* Investment Summary */}
        <div className="bg-[var(--primary)] rounded-2xl shadow-2xl overflow-hidden">
          <div className="p-10 text-center text-[var(--background)]">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Sun className="w-10 h-10 animate-pulse" />
              <h3 className="text-3xl font-bold">Your Paradise Package</h3>
              <Sun className="w-10 h-10 animate-pulse" />
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 mb-8">
              <div className="text-[var(--background)]/80 text-sm uppercase tracking-widest mb-3">Total Investment</div>
              <div className="text-6xl font-bold mb-2">{formatCurrency(quote.totalPrice)}</div>
              <p className="text-[var(--background)]/80">All-inclusive tropical getaway</p>
            </div>

            {quote.depositAmount > 0 && (
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <div className="text-[var(--background)]/80 text-xs uppercase tracking-widest mb-2">Initial Deposit</div>
                  <div className="text-3xl font-bold">{formatCurrency(quote.depositAmount)}</div>
                </div>
                {quote.depositDueDate && (
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="text-[var(--background)]/80 text-xs uppercase tracking-widest mb-2">Due Date</div>
                    <div className="text-2xl font-semibold">{formatDate(quote.depositDueDate)}</div>
                  </div>
                )}
              </div>
            )}

            {/* Payment Button */}
            {onPayment && (
              <div>
                <button
                  onClick={onPayment}
                  disabled={isAgentPreview}
                  className={`w-full bg-white text-[var(--primary)] font-bold py-5 px-8 rounded-xl text-lg transition-all duration-300 flex items-center justify-center gap-3 ${
                    isAgentPreview ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[var(--muted)] shadow-xl hover:shadow-2xl hover:scale-105'
                  }`}
                >
                  <CheckCircle2 className="w-6 h-6" />
                  {isAgentPreview ? 'Preview Mode' : 'Book Your Escape'}
                  <Palmtree className="w-6 h-6" />
                </button>
                {!isAgentPreview && (
                  <p className="text-[var(--background)]/80 text-sm mt-4">
                    🌴 Secure booking • Flexible cancellation • 24/7 concierge
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Agent Contact Footer */}
        {(quote.agent_name || quote.agent_email || quote.agent_phone) && (
          <div className="mt-12 bg-[var(--muted)] rounded-3xl border-2 border-[var(--border)] overflow-hidden shadow-xl">
            <div className="bg-[var(--primary)] px-6 py-4">
              <div className="flex items-center justify-center gap-2 text-[var(--background)]">
                <Palmtree className="w-6 h-6" />
                <h3 className="text-lg font-bold">Your Paradise Planner</h3>
              </div>
            </div>
            
            <div className="p-6">
              {quote.agent_name && (
                <div className="text-center mb-4">
                  <p className="text-xl font-bold text-[var(--foreground)]">{quote.agent_name}</p>
                  {quote.agent_business_name && (
                    <p className="text-[var(--muted-foreground)] font-semibold">{quote.agent_business_name}</p>
                  )}
                </div>
              )}
              
              <div className="space-y-3 max-w-md mx-auto">
                {quote.agent_email && (
                  <a 
                    href={`mailto:${quote.agent_email}`}
                    className="flex items-center gap-3 bg-white px-4 py-3 rounded-2xl hover:bg-[var(--muted)] transition-colors border border-[var(--border)] shadow-sm"
                  >
                    <Mail className="w-5 h-5 text-[var(--muted-foreground)]" />
                    <span className="text-[var(--foreground)] font-medium">{quote.agent_email}</span>
                  </a>
                )}
                
                {quote.agent_phone && (
                  <a 
                    href={`tel:${quote.agent_phone}`}
                    className="flex items-center gap-3 bg-white px-4 py-3 rounded-2xl hover:bg-[var(--muted)] transition-colors border border-[var(--border)] shadow-sm"
                  >
                    <Phone className="w-5 h-5 text-[var(--muted-foreground)]" />
                    <span className="text-[var(--foreground)] font-medium">{quote.agent_phone}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="flex items-center justify-center gap-3 text-[var(--muted-foreground)]/60">
            <Waves className="w-5 h-5" />
            <span className="text-sm uppercase tracking-widest">Tropical Escapes • Paradise Found</span>
            <Waves className="w-5 h-5" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TropicalEscapeTemplate;
